<template>
  <transition name="legal-sheet">
    <div
      v-if="visible"
      class="legal-sheet"
      :class="{ 'legal-sheet--page': page }"
      :dir="isRtl ? 'rtl' : 'ltr'"
      :role="page ? null : 'dialog'"
      :aria-modal="page ? null : 'true'"
      :aria-label="$t('legal.center')"
      @click.self="onBackdrop"
    >
      <div class="legal-sheet__panel">
        <!-- Başlık -->
        <header class="legal-sheet__head">
          <div class="legal-sheet__head-text">
            <span class="legal-sheet__eyebrow">{{ $t('legal.center') }}</span>
            <h2 class="legal-sheet__title">
              {{ title }}
            </h2>
          </div>
          <button
            type="button"
            class="legal-sheet__close"
            :aria-label="$t('loginConsent.close')"
            @click="close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <!-- Sekmeler -->
        <nav
          ref="tabs"
          class="legal-sheet__tabs"
          role="tablist"
        >
          <button
            v-for="tab in tabs"
            :key="tab.doc"
            type="button"
            role="tab"
            class="legal-sheet__tab"
            :class="{ active: tab.doc === doc }"
            :aria-selected="tab.doc === doc ? 'true' : 'false'"
            @click="select(tab.doc)"
          >
            {{ tab.label }}
          </button>
        </nav>

        <!-- İçerik -->
        <div
          ref="body"
          class="legal-sheet__body"
        >
          <!-- Boho'nun eski sözleşme metinleri (Ayarlar > Üyelik Sözleşmeleri ile aynı içerik) -->
          <contracts
            v-if="source && source.type === 'old'"
            :key="source.lang + source.section"
            :lang="source.lang"
            :section="source.section"
            embedded
          />

          <!-- Eski metni olmayan belgeler: güncel metin -->
          <div
            v-else-if="source && source.type === 'new'"
            class="legal-sheet__doc"
            lang="en"
            dir="ltr"
            @click="onBodyClick"
          >
            <div
              v-if="state === 'loading'"
              class="legal-sheet__status"
            >
              <b-spinner small />
            </div>
            <p
              v-else-if="state === 'error'"
              class="legal-sheet__status"
            >
              {{ $t('legal.loadError') }}
            </p>
            <template v-else>
              <section
                v-for="(sec, index) in sections"
                :key="index"
              >
                <h3>{{ index + 1 }}. {{ sec.heading }}</h3>
                <p v-html="sec.html" />
              </section>
            </template>
          </div>

          <!-- İçerik bildir -->
          <div
            v-else-if="source && source.type === 'report'"
            class="legal-sheet__report"
          >
            <p>{{ $t('legal.report.intro') }}</p>
            <section class="legal-sheet__card">
              <h3>{{ $t('legal.report.contentTitle') }}</h3>
              <p>{{ $t('legal.report.contentBody') }}</p>
              <a
                class="legal-sheet__mail"
                :href="mailto($t('legal.report.contentSubject'))"
              >{{ info.email }}</a>
            </section>
            <section class="legal-sheet__card legal-sheet__card--alert">
              <h3>{{ $t('legal.report.illegalTitle') }}</h3>
              <p>{{ $t('legal.report.illegalBody') }}</p>
              <a
                class="legal-sheet__mail"
                :href="mailto($t('legal.report.illegalSubject'))"
              >{{ info.email }}</a>
            </section>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import Contracts from '@/views/settings/components/Contracts.vue'
import { LEGAL_INFO, loadLegalContent, renderLegalText } from '@/libs/legal'

// Giriş ekranındaki bağlantılar -> gösterilecek metin.
// Türkçe için Boho'nun eski Türkçe metinleri, diğer dillerde eski İngilizce metinler kullanılır.
// Eski metni olmayan belgeler güncel (İngilizce) metinden gösterilir.
const OLD_SECTIONS = {
  'membership-agreement': { tr: 'uyelik-sozlesmesi', en: 'membership-agreement' },
  'terms-of-service': { tr: 'gizlilik-ve-kullanim-kosullari', en: null },
  'privacy-policy': { tr: 'kisisel-verilerin-kullanimi-politikasi', en: 'privacy-policy' },
}

const OLD_TITLES = {
  'uyelik-sozlesmesi': 'Üyelik Sözleşmesi',
  'gizlilik-ve-kullanim-kosullari': 'Gizlilik ve Kullanım Koşulları',
  'kisisel-verilerin-kullanimi-politikasi': 'Kişisel Verilerin Kullanımı Politikası',
  'membership-agreement': 'Membership Agreement',
  'privacy-policy': 'Privacy Policy',
}

const DOC_KEYS = {
  'membership-agreement': 'membershipAgreement',
  'terms-of-service': 'termsOfService',
  'privacy-policy': 'privacyPolicy',
  'community-guidelines': 'communityGuidelines',
  'content-safety': 'contentSafety',
  'child-safety': 'childSafety',
}

const TAB_ORDER = ['membership-agreement', 'terms-of-service', 'privacy-policy', 'community-guidelines', 'child-safety', 'report']

export default {
  name: 'LoginLegalModal',
  components: { BSpinner, Contracts },
  props: {
    // true: giriş yapmadan açılan ayrı sayfa (/agreements/:doc). false: açılır pencere
    page: {
      type: Boolean,
      default: false,
    },
    initialDoc: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      visible: this.page,
      doc: null,
      state: 'loading',
      raw: [],
      info: LEGAL_INFO,
      prevOverflow: '',
    }
  },
  computed: {
    isRtl() {
      return this.$i18n.locale === 'ar'
    },
    tabs() {
      return TAB_ORDER.map(doc => ({
        doc,
        label: doc === 'report' ? this.$t('legal.reportContent') : this.$t(`legal.docs.${DOC_KEYS[doc]}`),
      }))
    },
    source() {
      if (!this.doc) return null
      if (this.doc === 'report') return { type: 'report' }
      const old = OLD_SECTIONS[this.doc]
      if (old) {
        const lang = this.$i18n.locale === 'tr' ? 'tr' : 'en'
        if (old[lang]) return { type: 'old', lang, section: old[lang] }
      }
      return { type: 'new', slug: this.doc }
    },
    title() {
      const src = this.source
      if (!src) return ''
      if (src.type === 'report') return this.$t('legal.reportContent')
      if (src.type === 'old') return OLD_TITLES[src.section]
      return DOC_KEYS[src.slug] ? this.$t(`legal.docs.${DOC_KEYS[src.slug]}`) : ''
    },
    sections() {
      return this.raw.map(([heading, text]) => ({ heading, html: renderLegalText(text, this.info) }))
    },
  },
  watch: {
    // Sayfa modunda adres değişince (geri/ileri) ilgili sekmeyi aç
    initialDoc(doc) {
      if (this.page && doc && doc !== this.doc) this.select(doc, true)
    },
    visible(open) {
      if (this.page) return
      // Sayfa kaydırmasını kilitle; kapanınca önceki durumu geri getir (yaş onayı açıksa kilitli kalsın)
      if (open) {
        this.prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = this.prevOverflow || ''
      }
      if (open) document.addEventListener('keydown', this.onKey)
      else document.removeEventListener('keydown', this.onKey)
    },
  },
  created() {
    if (this.page) this.select(TAB_ORDER.includes(this.initialDoc) ? this.initialDoc : TAB_ORDER[0], true)
  },
  beforeDestroy() {
    if (this.visible) document.body.style.overflow = this.prevOverflow || ''
    document.removeEventListener('keydown', this.onKey)
  },
  methods: {
    // Dışarıdan çağrılır: this.$refs.legalModal.open('privacy-policy')
    open(doc) {
      this.visible = true
      this.select(doc)
    },
    select(doc, fromRoute = false) {
      this.doc = doc
      if (this.page && !fromRoute && this.$route.params.doc !== doc) {
        this.$router.replace({ name: 'public-legal', params: { doc }, query: this.$route.query }).catch(() => {})
      }
      if (this.source && this.source.type === 'new') this.loadNew()
      this.$nextTick(() => {
        if (this.$refs.body) this.$refs.body.scrollTop = 0
        if (this.page) window.scrollTo(0, 0)
        const { tabs } = this.$refs
        const active = tabs && tabs.querySelector('.active')
        if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'center' })
      })
    },
    close() {
      if (this.page) {
        // Sayfa modunda giriş ekranına dön
        if (window.history.length > 1) this.$router.back()
        else this.$router.push({ name: 'auth-login' })
        return
      }
      this.visible = false
    },
    onBackdrop() {
      if (!this.page) this.close()
    },
    onKey(event) {
      if (this.page) return
      if (event.key === 'Escape') this.close()
    },
    loadNew() {
      const { slug } = this.source
      this.state = 'loading'
      loadLegalContent('en')
        .then(content => {
          if (!this.source || this.source.slug !== slug) return
          this.raw = content[slug] || []
          this.state = 'ready'
        })
        .catch(() => { this.state = 'error' })
    },
    // Metin içindeki diğer sözleşme bağlantıları aynı sayfada ilgili sekmeyi açsın
    onBodyClick(event) {
      const link = event.target.closest && event.target.closest('a[data-legal-link]')
      if (!link) return
      event.preventDefault()
      this.select(link.getAttribute('data-legal-link'))
    },
    mailto(subject) {
      return `mailto:${this.info.email}?subject=${encodeURIComponent(subject)}`
    },
  },
}
</script>

<style lang="scss">
.legal-sheet {
  position: fixed;
  inset: 0;
  z-index: 1060; // yaş onayı ve çerez panelinin üstünde
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(8, 4, 24, 0.78);
  backdrop-filter: blur(8px);
}

.legal-sheet__panel {
  width: 100%;
  max-width: 920px;
  height: min(86vh, 860px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  background: #160d36;
  border: 1px solid rgba(150, 110, 255, 0.3);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
  color: rgba(236, 232, 255, 0.88);
}

.legal-sheet__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px 14px;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(234, 3, 178, 0.28), transparent 60%),
    linear-gradient(180deg, #241750, #160d36);
}

.legal-sheet__eyebrow {
  display: block;
  margin-bottom: 4px;
  color: #f472b6;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.legal-sheet__title {
  margin: 0;
  color: #fff !important;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
}

.legal-sheet__close {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
}

.legal-sheet__tabs {
  display: flex;
  gap: 8px;
  padding: 4px 26px 14px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid rgba(150, 110, 255, 0.2);
  background: #160d36;

  &::-webkit-scrollbar {
    display: none;
  }
}

.legal-sheet__tab {
  flex-shrink: 0;
  padding: 9px 16px;
  border-radius: 999px;
  border: 1px solid rgba(150, 110, 255, 0.3);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(236, 232, 255, 0.8);
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #fff;
    border-color: rgba(244, 114, 182, 0.55);
  }

  &.active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #7c3aed, #ea03b2);
  }
}

.legal-sheet__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 26px 32px;

  h3 {
    color: #fff !important;
    font-size: 17px;
    font-weight: 700;
    margin: 20px 0 8px;
  }

  p,
  li {
    color: rgba(236, 232, 255, 0.86);
    font-size: 15px;
    line-height: 1.75;
  }

  a {
    color: #f472b6;
  }
}

.legal-sheet__status {
  display: flex;
  justify-content: center;
  padding: 32px 0;
}

.legal-sheet__report {
  padding-top: 14px;
}

.legal-sheet__card {
  margin-top: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(150, 110, 255, 0.25);
  background: rgba(255, 255, 255, 0.03);

  h3 {
    margin-top: 0;
  }
}

.legal-sheet__card--alert {
  border-color: rgba(244, 114, 182, 0.45);
}

.legal-sheet__mail {
  display: inline-block;
  margin-top: 4px;
  padding: 8px 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7c3aed, #ea03b2);
  color: #fff !important;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none !important;
}

.legal-sheet-enter-active,
.legal-sheet-leave-active {
  transition: opacity 0.2s ease;

  .legal-sheet__panel {
    transition: transform 0.25s ease;
  }
}

.legal-sheet-enter,
.legal-sheet-leave-to {
  opacity: 0;

  .legal-sheet__panel {
    transform: translateY(24px);
  }
}

// Telefonda tam ekran
@media (max-width: 767px) {
  .legal-sheet {
    padding: 0;
    align-items: stretch;
  }

  .legal-sheet__panel {
    max-width: none;
    height: 100%;
    height: 100dvh;
    border-radius: 0;
    border: 0;
  }

  .legal-sheet__head {
    padding: calc(16px + env(safe-area-inset-top, 0px)) 16px 12px;
  }

  .legal-sheet__title {
    font-size: 19px;
  }

  .legal-sheet__tabs {
    padding: 2px 16px 12px;
  }

  .legal-sheet__body {
    padding: 4px 16px calc(28px + env(safe-area-inset-bottom, 0px));

    p,
    li {
      font-size: 14.5px;
    }
  }
}
// Ayrı sayfa modu (giriş yapmadan açılan /agreements sayfası)
.legal-sheet.legal-sheet--page {
  position: static;
  min-height: 100vh;
  min-height: 100dvh;
  align-items: flex-start;
  padding: 32px 16px 48px;
  backdrop-filter: none;
  background:
    radial-gradient(1200px 600px at 85% -10%, rgba(234, 3, 178, 0.18), transparent 60%),
    radial-gradient(900px 500px at -10% 10%, rgba(124, 58, 237, 0.22), transparent 60%),
    #0f0826;

  .legal-sheet__panel {
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  .legal-sheet__head {
    border-radius: 24px 24px 0 0;
  }

  .legal-sheet__tabs {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .legal-sheet__body {
    overflow: visible;
    flex: none;
  }
}

@media (max-width: 767px) {
  .legal-sheet.legal-sheet--page {
    padding: 0;

    .legal-sheet__panel {
      min-height: 100vh;
      min-height: 100dvh;
    }

    .legal-sheet__head {
      border-radius: 0;
    }
  }
}
</style>
